import HOOK from "../../components/hooks"
import { COLUMN, FORMATTING, JARGON, LAYOUT, TENSE } from "../../modules/constants"
import { CHECKBOX } from "../../modules/checkbox_constants"
import { INDEX } from "../../modules/indexConstants"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { shortMonthNameFromNumber, formatFloat } from "../../modules/utilities"
import { initStats2 } from "../../components/layout/InitStats"

const { birthYear, border1, float, integer, left, percent, right, string } = FORMATTING
const stats = {}
const borderLeft = border1
const rolesWidth = "8%"
const nationalityWidth = "17%"
const residentofWidth = "15%"
const dobWidth = "8%"
const ageSexWidth = "8%"

//#region Header
export const createLsePeopleHeadersData = props => {
    // console.log('createLsePeopleHeadersData props:', props)
    const { checkboxes, portrait, the350club } = props

    let apptsCountHead = []

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense)
        apptsCountHead[tense] = headCell({
            id: `appts${tense}`,
            label: `${TENSE[tense].NAME}`,
            width: rolesWidth,
        })

    const star = headCell({
        align: left,
        id: COLUMN.STAR,
        type: string,
        width: LAYOUT.STAR_WIDTH,
    })

    const officer = headCell({
        align: left,
        colSpan: portrait ? 2 : 1,
        id: COLUMN.OFFICER,
        label: "Name",
        paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
    })

    const roles = headCell({
        id: COLUMN.SKIP_ME,
        label: `${JARGON.ROLES}:`,
        width: "5%",
        sortLabel: false,
    })

    const age = headCell({
        id: COLUMN.AGE,
        width: ageSexWidth
    })

    const dob = headCell({
        id: COLUMN.DOB,
        width: dobWidth
    })

    const nationality = headCell({
        align: left,
        borderLeft,
        id: COLUMN.NATIONALITY,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        width: nationalityWidth,
    })

    const peopleSex = headCell({
        id: COLUMN.SEX,
        width: ageSexWidth,
    })

    const residence = headCell({
        align: left,
        id: COLUMN.RESIDENCE,
        width: residentofWidth,
    })

    const headCells = portrait
        ? [star, officer, age]
        : [star, officer, roles]

    if (!portrait) {
        pushApptsCountsToHeadCells({ checkboxes, headCells })
        headCells.push(nationality, residence, dob, age, peopleSex)
    }

    if (the350club)
        headCells.shift() // Remove star

    addPaddingRightToLastCell(headCells)

    return headCells
}

const pushApptsCountsToHeadCells = ({ checkboxes, headCells }) => {
    const apptsCountHead = getApptsCountHead()

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense) {
        if (tenseBitSelected({ tense, checkboxes }))
            headCells.push(apptsCountHead[tense])
    }

    // Add padding to the last apptsCount cell - because the next column is aligned left
    addPaddingRightToLastCell(headCells)
}

const getApptsCountHead = () => {
    const apptsCountHead = []

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense)
        apptsCountHead[tense] = headCell({
            id: `appts${tense}`,
            label: `${TENSE[tense].NAME}`,
            width: rolesWidth,
        })

    return apptsCountHead
}
//#endregion Header

//#region Footers
export const createLsePeopleFootersData = props => {
    // console.log('createLsePeopleFootersData props:',props)

    const { checkboxes, portrait, the350club } = props
    let column = null

    for (const property in stats)
        calcStats(stats[property])

    column = COLUMN.STAR
    const starFoot = {
        total: getCell({ align: left, column }),
        mean: getCell({ align: left, column })
    }

    column = COLUMN.LEGEND
    const legend = {
        total: getCell({ column, label: "Total:" }),
        mean: getCell({ column, label: "Average:" }),
    }

    column = COLUMN.OFFICER
    const value = stats[column].total
    const nameFoot = {
        total: getCell({
            align: left,
            column,
            paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
            suffix: ` ${value === 1 ? JARGON.PERSON : JARGON.PEOPLE}`,
            type: integer,
            value,
        }),
        mean: getCell({ column })
    }

    column = COLUMN.NATIONALITY
    const nationality = {
        total: getCell({
            align: left,
            borderLeft,
            column,
            paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        }),
        mean: getCell({
            align: left,
            borderLeft,
            column,
            paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        }),
    }

    column = COLUMN.RESIDENCE
    const residence = {
        total: getCell({
            column,
            align: left,
        }),
        mean: getCell({
            column,
            align: left,
        }),
    }

    column = COLUMN.AGE
    const age = {
        total: getCell({
            column,
            type: integer,
            value: stats[column].total,
        }),
        mean: getCell({
            column,
            type: float,
            value: stats[column].mean,
        }),
    }

    column = COLUMN.DOB
    const dob = {
        total: getCell({
            column,
        }),
        mean: getCell({
            column,
            type: birthYear,
            value: stats[COLUMN.AGE].mean,
        }),
    }

    column = COLUMN.SEX
    const sex = {
        total: getCell({
            column,
            suffix: " F",
            type: integer,
            value: stats[column].total,
        }),
        mean: getCell({
            column,
            type: percent,
            value: stats[column].mean,
            suffix: "% F",
        }),
    }

    const footCells = {
        totals: [starFoot.total, nameFoot.total],
        means: [starFoot.mean, nameFoot.mean],
    }

    if (!portrait) {
        // Add the "Total" and "Average" legends
        footCells.totals.push(legend.total)
        footCells.means.push(legend.mean)

        // Add the appointment counts - Current, Past etc
        pushApptsCountToFootCells({ footCells, checkboxes })

        // Add padding to the last apptsCount cell - because the next column is aligned left
        addPaddingRightToLastCell(footCells.totals)
        addPaddingRightToLastCell(footCells.means)

        // Add other cells
        footCells.totals.push(nationality.total, residence.total, dob.total, age.total, sex.total)
        footCells.means.push(nationality.mean, residence.mean, dob.mean, age.mean, sex.mean)
    } else {
        // If profile, only add the legend and and age mean values.
        footCells.totals.push(legend.mean, age.mean)
        footCells.means.push(legend.mean, age.mean)
    }

    if (the350club) {
        footCells.totals.shift() // Remove starFoot
        footCells.means.shift()
    }

    // Add right padding to the rightmost cell
    addPaddingRightToLastCell(footCells.totals)
    addPaddingRightToLastCell(footCells.means)
    return footCells
}

const pushApptsCountToFootCells = ({ footCells, checkboxes }) => {
    const apptsCount = getApptsCountFootCells()

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense) {
        if (tenseBitSelected({ tense, checkboxes })) {
            footCells.totals.push(apptsCount[tense].total)
            footCells.means.push(apptsCount[tense].mean)
        }
    }
}

const getApptsCountFootCells = () => {
    // Returns and array of objects corresponding to the 4 CHECKBOX[HOOK.TENSE] items (current, notice,...)
    const apptsCount = []

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense) {
        if (stats.apptsCount[tense].numof > 0)
            stats.apptsCount[tense].mean = stats.apptsCount[tense].total / stats.apptsCount[tense].numof

        apptsCount[tense] = {
            total: getCell({
                column: `appts${tense}`,
                type: integer,
                value: stats.apptsCount[tense].total,
            }),
            mean: getCell({
                column: `appts${tense}`,
                type: float,
                precision: 2,
                // value: stats.apptsCount[tense].mean,

                label: formatFloat(stats.apptsCount[tense].mean, 2)
            }),
        }
    }

    return apptsCount
}
//#endregion Footers

//#region Rows
export const createLsePeopleRowData = props => {
    // console.log('createLsePeopleRowData props:',props)
    const { items } = props
    const rows = []
    initStats2(stats)
    items.forEach(item => {
        rows.push(createLsePeopleRow({ ...props, item }))
    })

    return rows
}

const createLsePeopleRow = props => {
    // console.log('createLsePeopleRow props:',props)

    const { checkboxes, item, portrait, the350club } = props
    const { apptStats, commonInfo, index, nameToSort, personInfo } = item

    let name = getCellWithTooltip({
        colSpan: 2, // Header and footers have a 'Legend' column (COLUMN.LEGEND)
        paddingLeft: the350club ? LAYOUT.HOME.PADDING.BORDER : 0,
        sortValue: nameToSort,
        type: string,
        value: commonInfo.name,
    })
    setStats(1, stats[COLUMN.OFFICER], true)

    const dob = personInfo.dateOfBirth
        ? `${shortMonthNameFromNumber(personInfo.dateOfBirth.month)} ${personInfo.dateOfBirth.year}`
        : JARGON.UNKNOWN

    const dobCell = getCellWithTooltip({
        align: right,
        type: string,
        value: dob,
    })

    const age = getCellWithTooltip({ value: personInfo.age })
    setStats(personInfo.age, stats[COLUMN.AGE], true)

    const sexCell = getCellWithTooltip(
        personInfo.sex === 1
            ? {
                align: right,
                tooltip: "female",
                type: string,
                value: "F",
            }
            : {
                align: right,
                tooltip: personInfo.sex === 0 ? "male" : "unknown",
                type: string,
                value: personInfo.sex === 0 ? "M" : JARGON.UNKNOWN
            }
    )

    if (personInfo.sex === 0 || personInfo.sex === 1) {
        stats[COLUMN.SEX].total += personInfo.sex
        stats[COLUMN.SEX].numof++
    }

    const nationality = getCellWithTooltip({
        align: left,
        borderLeft,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        type: string,
        value: personInfo.nationality,
    })

    const residence = getCellWithTooltip({
        align: left,
        type: string,
        value: personInfo.countryOfResidence,
    })

    const apptsCount = getApptsCountRow({ apptStats, checkboxes })

    const rowCells = portrait
        ? [name, age]
        : [name]

    if (!portrait) {
        for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense)
            if (tenseBitSelected({ tense, checkboxes }))
                rowCells.push(apptsCount[tense])

        // Add padding to the last apptsCount cell - because the next column is aligned left
        addPaddingRightToLastCell(rowCells)

        rowCells.push(nationality, residence, dobCell, age, sexCell)
    }

    addPaddingRightToLastCell(rowCells)

    return {
        index,
        link: commonInfo.officerIds ? `/person/${commonInfo.officerIds.join()}` : null,
        isFavourite: item.isFavourite,
        cellWithTooltips: rowCells,
    }
}

const getApptsCountRow = props => {
    const apptsCount = []

    for (let tense = 0; tense < CHECKBOX[HOOK.TENSE].length; ++tense) {
        const count = getCount({ ...props, tense })

        apptsCount[tense] = getCellWithTooltip({
            color: TENSE[tense].COLOR,
            value: count,
        })

        setStats(count, stats.apptsCount[tense], true)
    }
    return apptsCount
}

const getCount = ({ apptStats, checkboxes, tense }) => {
    let count = 0
    for (let index = 0; index < CHECKBOX[HOOK.LSE_INDEX].length; index++) {
        // If at least one tense selected and the CHECKBOX[HOOK.LSE_INDEX][index] are checked
        if (tenseBitSelected({ tense, checkboxes }) && CHECKBOX[HOOK.LSE_INDEX][index].BIT & checkboxes[HOOK.LSE_INDEX]) {

            // If Type/Trading company is checked add apptStats.counts for this tense and index
            if (CHECKBOX[HOOK.COMPANY_TYPE][INDEX.CO_TYPE.TRADE].BIT & checkboxes[HOOK.COMPANY_TYPE])
                count += apptStats.counts[tense][index]

            // If Type/Investment company is checked add apptStats.aicCounts for this tense and index
            if (CHECKBOX[HOOK.COMPANY_TYPE][INDEX.CO_TYPE.INVEST].BIT & checkboxes[HOOK.COMPANY_TYPE])
                count += apptStats.aicCounts[tense][index]
        }
    }

    return count
}
//#endregion Rows

const tenseBitSelected = ({ tense, checkboxes }) => CHECKBOX[HOOK.TENSE][tense].BIT & checkboxes[HOOK.TENSE]


